import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { v4 as uuid } from 'uuid';
import { HiringSite } from 'eve-account-client';
import { Alert } from '@ver-uds/uswds-react';
import { useEnrollment } from '../../../context/Enrollment/EnrollmentContext';
import { EnrollmentData } from '../EnrollmentData';
import FormVariant from '../../../components/Enrollment/FormVariant';
import SubmitAction from '../../../components/Enrollment/SubmitAction';
import { removeEmptyStrings } from '../../../utils/util';
import { CompanyAddressesFormData } from '../../../components/Enrollment/CompanyAddresses/CompanyAddressesFormData';
import HelpModalContent from '../../../constants/HelpModalContent';
import EnrollmentStepperButtons from '../../../components/Enrollment/EnrollmentStepperButtons';
import UploadErrors from '../../../components/HiringSites/BulkUpload/UploadErrors';
import { ValidationResult } from '../../../components/HiringSites/BulkUpload/HiringSiteValidator';
import HiringSitesForm from '../../../components/HiringSites/HiringSitesForm';

const HiringSites = (): JSX.Element => {
  const { nextPage, enrollmentData, setEnrollmentData } = useEnrollment();
  const [uploadResults, setUploadResults] = useState<ValidationResult | undefined>(undefined);
  const pageTitle = 'Hiring Sites';
  const onNext = () => {
    const totalHiringSites = enrollmentData.hiringSitesFormData?.length || 0;
    if (totalHiringSites > 0) {
      nextPage();
    }
  };
  useEffect(() => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const existingHiringSites = prevState.hiringSitesFormData || [];
      const companyAddressesFormData = prevState.companyAddressesFormData as CompanyAddressesFormData;

      const defaultHiringSite: HiringSite = {
        id: uuid(),
        line1: companyAddressesFormData.physicalLocation.line1,
        line2: companyAddressesFormData.physicalLocation.line2,
        city: companyAddressesFormData.physicalLocation.city,
        stateCode: companyAddressesFormData.physicalLocation.stateCode,
        zipCode: companyAddressesFormData.physicalLocation.zipCode,
        countyId: companyAddressesFormData.physicalLocation.countyId,
      };

      const updatedHiringSites = [...existingHiringSites];

      if (prevState.defaultHiringSiteCreated === false) {
        updatedHiringSites.push(defaultHiringSite);
      }

      return {
        ...prevState,
        hiringSitesFormData: updatedHiringSites,
        defaultHiringSiteCreated: true,
      };
    });
  }, [setEnrollmentData]);

  const onAdd = (hiringSites: HiringSite[]) => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const updatedHiringSites = prevState.hiringSitesFormData || [];

      hiringSites.forEach((site) => {
        updatedHiringSites.push({
          ...site,
          id: uuid(),
        });
      });

      return {
        ...prevState,
        hiringSitesFormData: updatedHiringSites,
      };
    });
  };

  const onBulkAdd = (hiringSites: HiringSite[], bulkUploadErrors: HiringSite[]) => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const updatedHiringSites = prevState.hiringSitesFormData || [];

      hiringSites.forEach((site) => {
        updatedHiringSites.push({
          ...site,
          id: uuid(),
        });
      });

      return {
        ...prevState,
        hiringSitesFormData: updatedHiringSites,
        bulkUploadErrors,
      };
    });
  };

  const onDelete = (hiringSite: HiringSite) => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const updatedHiringSites = prevState.hiringSitesFormData?.filter((site) => site.id !== hiringSite.id);
      return {
        ...prevState,
        hiringSitesFormData: updatedHiringSites,
      };
    });
  };

  const onUpdate = (hiringSite: HiringSite) => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const index = prevState.hiringSitesFormData?.findIndex((site) => site.id === hiringSite.id) || 0;
      const updatedHiringSites = prevState.hiringSitesFormData || [];
      updatedHiringSites[index] = hiringSite;
      return {
        ...prevState,
        hiringSitesFormData: updatedHiringSites,
      };
    });
  };
  const onSubmit = (action: SubmitAction, hiringSites: HiringSite[], uploadErrors: HiringSite[]) => {
    removeEmptyStrings(hiringSites);
    if (action === SubmitAction.ADD) {
      onAdd(hiringSites);
      setUploadResults({ validSites: hiringSites, invalidSites: [] });
    }
    if (action === SubmitAction.BULK_UPLOAD) {
      onBulkAdd(hiringSites, uploadErrors);
      setUploadResults({ validSites: hiringSites, invalidSites: uploadErrors });
    }
    if (action === SubmitAction.UPDATE) {
      onUpdate(hiringSites[0]);
      setUploadResults(undefined);
    }
    if (action === SubmitAction.DELETE) {
      onDelete(hiringSites[0]);
      setUploadResults(undefined);
    }
  };
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Hiring Sites',
    });
  }, []);
  return (
    <section className="padding-y-4">
      <h1 className="margin-bottom-4">{pageTitle}</h1>
      <p>{HelpModalContent.hiringSites.body}</p>
      <Alert className="grid-col-12 desktop:grid-col-9 margin-y-3" status="info" slim>
        <Alert.Text>Hiring sites can be added or deleted at any time after enrollment is complete.</Alert.Text>
      </Alert>
      <HiringSitesForm
        variant={FormVariant.ENROLLMENT}
        hiringSitesData={(enrollmentData.hiringSitesFormData as HiringSite[]) || []}
        onSubmit={onSubmit}
        UploadErrors={() => (
          <UploadErrors
            variant={FormVariant.ENROLLMENT}
            uploadErrors={(enrollmentData.bulkUploadErrors as HiringSite[]) || []}
            uploadResults={uploadResults}
          />
        )}
      />
      <EnrollmentStepperButtons nextButtonType="submit" onNext={onNext} />
    </section>
  );
};

export default HiringSites;
